import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, 
  ChevronRight, 
  MapPin, 
  Phone, 
  Mail, 
  Building, 
  TrendingUp, 
  Car,
  Heart,
  Wifi,
  Users,
  BookOpen,
  Settings,
  Home,
  Zap,
  Shield,
  Clock,
  Star,
  Target,
  Award,
  Briefcase,
  Globe
} from 'lucide-react';
import { useScrollReveal, useStaggeredScrollReveal } from '../hooks/useScrollReveal';
import { useLanguage } from '../contexts/LanguageContext';
import { useContent } from '../contexts/ContentContext';
import Navbar from '../components/Navbar';
import { landingTranslations } from '../translations/landingTranslations';
import { getContentTranslation } from '../translations/contentTranslations';
import { poisAPI } from '../services/api';
import { POI } from '../types';
import './LandingPage.css';

interface SlideData {
  id: number;
  image: string;
  title: string;
  subtitle: string;
}

interface FacilityData {
  id: number;
  icon: React.ReactNode;
  title: string;
  description: string;
}

interface OpportunityData {
  id: number;
  image: string;
  title: string;
  description: string;
}

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentLanguage } = useLanguage();
  const { slides, profileContent, facilities, investmentOpportunities, websiteSettings, footerContent } = useContent();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isNavScrolled, setIsNavScrolled] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const [selectedAdvantage, setSelectedAdvantage] = useState<any>(null);
  const [showAdvantageModal, setShowAdvantageModal] = useState(false);
  const [featuredPOIs, setFeaturedPOIs] = useState<POI[]>([]);
  const [currentInvestmentOpportunitySlide, setCurrentInvestmentOpportunitySlide] = useState(0);
  const [currentOpportunitySlide, setCurrentOpportunitySlide] = useState(0);
  const [slideAnimations, setSlideAnimations] = useState<string[]>([]);
  
  // Get translations for current language
  const t = landingTranslations[currentLanguage] || landingTranslations.id;
  
  // Random animation classes
  const animationClasses = [
    'slide-animation-zoom-in',
    'slide-animation-zoom-out', 
    'slide-animation-pan-left',
    'slide-animation-pan-right',
    'slide-animation-pan-up',
    'slide-animation-pan-down',
    'slide-animation-zoom-in-pan-left',
    'slide-animation-zoom-out-pan-right'
  ];
  
  // Generate random animation for each slide
  const generateRandomAnimations = (slidesCount: number) => {
    const animations: string[] = [];
    for (let i = 0; i < slidesCount; i++) {
      const randomIndex = Math.floor(Math.random() * animationClasses.length);
      animations.push(animationClasses[randomIndex]);
    }
    return animations;
  };

  // Scroll reveal hooks
  const profileReveal = useScrollReveal({ delay: 200 });
  const facilitiesReveal = useScrollReveal({ delay: 300 });
  const opportunitiesReveal = useScrollReveal({ delay: 400 });
  const investmentOpportunitiesReveal = useScrollReveal({ delay: 500 });
  const ctaReveal = useScrollReveal({ delay: 200 });
  
  const { setRef: setFacilityRef, visibleItems: visibleFacilities } = useStaggeredScrollReveal(9, 100);
  const { setRef: setOpportunityRef, visibleItems: visibleOpportunities } = useStaggeredScrollReveal(6, 150);

  // Slides data now comes from ContentContext
  const activeSlides = slides.filter(slide => slide.isActive);
  
  // Initialize random animations for slides
  useEffect(() => {
    if (activeSlides.length > 0) {
      const animations = generateRandomAnimations(activeSlides.length);
      setSlideAnimations(animations);
      console.log('🎨 Generated slide animations:', animations);
    }
  }, [activeSlides.length]);
  
  // Debug logging to check slide data structure
  useEffect(() => {
    if (activeSlides.length > 0) {
      console.log('🎯 🎯 SLIDER DEBUG:');
      console.log('🎯 Language:', currentLanguage);
      console.log('🎯 First slide title:', activeSlides[0].title);
      console.log('🎯 Title type:', typeof activeSlides[0].title);
      if (typeof activeSlides[0].title === 'object') {
        console.log('🎯 Title ID:', activeSlides[0].title.id);
        console.log('🎯 Title EN:', activeSlides[0].title.en);
      }
      console.log('🎯 getContentTranslation result:', getContentTranslation(activeSlides[0].title, currentLanguage));
    }
  }, [activeSlides, currentLanguage]);

  // Facilities data now comes from ContentContext
  const activeFacilities = facilities.filter(facility => facility.isActive);
  
  // Helper function to get icon component from string
  const getFacilityIcon = (iconName: string) => {
    const iconMap: { [key: string]: React.ReactNode } = {
      'Car': <Car className="w-8 h-8" />,
      'MapPin': <MapPin className="w-8 h-8" />,
      'Building': <Building className="w-8 h-8" />,
      'BookOpen': <BookOpen className="w-8 h-8" />,
      'Heart': <Heart className="w-8 h-8" />,
      'Settings': <Settings className="w-8 h-8" />,
      'Wifi': <Wifi className="w-8 h-8" />,
      'Users': <Users className="w-8 h-8" />
    };
    return iconMap[iconName] || <Settings className="w-8 h-8" />;
  };

  // Helper function to get advantage icon from string (supports both FontAwesome class names and Lucide icon names)
  const getAdvantageIcon = (iconName: string) => {
    // FontAwesome to Lucide icon mapping
    const fontAwesomeToLucide: { [key: string]: React.ReactNode } = {
      'fas fa-map-marker-alt': <MapPin className="w-5 h-5 advantage-icon" />,
      'fas fa-map-pin': <MapPin className="w-5 h-5 advantage-icon" />,
      'fas fa-chart-line': <TrendingUp className="w-5 h-5 advantage-icon" />,
      'fas fa-trending-up': <TrendingUp className="w-5 h-5 advantage-icon" />,
      'fas fa-users': <Users className="w-5 h-5 advantage-icon" />,
      'fas fa-user-friends': <Users className="w-5 h-5 advantage-icon" />,
      'fas fa-bolt': <Zap className="w-5 h-5 advantage-icon" />,
      'fas fa-zap': <Zap className="w-5 h-5 advantage-icon" />,
      'fas fa-building': <Building className="w-5 h-5 advantage-icon" />,
      'fas fa-home': <Home className="w-5 h-5 advantage-icon" />,
      'fas fa-shield': <Shield className="w-5 h-5 advantage-icon" />,
      'fas fa-shield-alt': <Shield className="w-5 h-5 advantage-icon" />,
      'fas fa-clock': <Clock className="w-5 h-5 advantage-icon" />,
      'fas fa-star': <Star className="w-5 h-5 advantage-icon" />,
      'fas fa-target': <Target className="w-5 h-5 advantage-icon" />,
      'fas fa-award': <Award className="w-5 h-5 advantage-icon" />,
      'fas fa-briefcase': <Briefcase className="w-5 h-5 advantage-icon" />,
      'fas fa-globe': <Globe className="w-5 h-5 advantage-icon" />,
      'far fa-chart-bar': <TrendingUp className="w-5 h-5 advantage-icon" />,
      'fab fa-facebook': <Globe className="w-5 h-5 advantage-icon" />
    };

    // Direct Lucide icon mapping
    const lucideIconMap: { [key: string]: React.ReactNode } = {
      'MapPin': <MapPin className="w-5 h-5 advantage-icon" />,
      'TrendingUp': <TrendingUp className="w-5 h-5 advantage-icon" />,
      'Users': <Users className="w-5 h-5 advantage-icon" />,
      'Zap': <Zap className="w-5 h-5 advantage-icon" />,
      'Building': <Building className="w-5 h-5 advantage-icon" />,
      'Home': <Home className="w-5 h-5 advantage-icon" />,
      'Shield': <Shield className="w-5 h-5 advantage-icon" />,
      'Clock': <Clock className="w-5 h-5 advantage-icon" />,
      'Star': <Star className="w-5 h-5 advantage-icon" />,
      'Target': <Target className="w-5 h-5 advantage-icon" />,
      'Award': <Award className="w-5 h-5 advantage-icon" />,
      'Briefcase': <Briefcase className="w-5 h-5 advantage-icon" />,
      'Globe': <Globe className="w-5 h-5 advantage-icon" />
    };

    // Try FontAwesome mapping first, then Lucide mapping, then default
    return fontAwesomeToLucide[iconName] || lucideIconMap[iconName] || <TrendingUp className="w-5 h-5 advantage-icon" />;
  };

  // Investment opportunities data now comes from ContentContext
  const activeOpportunities = investmentOpportunities.filter(opportunity => opportunity.isActive);

  // Investment advantages now come from ContentContext
  const investmentAdvantages = profileContent.advantages;

  // Mock featured POIs data for demonstration (remove when real API is connected)
  const mockFeaturedPOIs: POI[] = [
    {
      _id: 'mock-1',
      name: 'Kawasan Industri Baki',
      description: 'Lokasi strategis untuk pengembangan industri manufaktur dengan fasilitas lengkap dan akses transportasi yang baik.',
      category: { _id: '1', name: 'Industrial Zone' } as any,
      layer: 'industrial',
      lat: -7.8,
      long: 110.8,
      address: 'Baki, Sukoharjo',
      images: [{ _id: '1', url: 'https://images.unsplash.com/photo-1565793298595-6a879b1d9492?w=600&h=400&fit=crop', publicId: 'mock', caption: 'Industrial Zone', isPrimary: true }],
      status: 2,
      links: [],
      tags: ['industry', 'manufacturing'],
      rating: 4.5,
      reviewCount: 25,
      isActive: true,
      isFeatured: true,
      createdAt: '2024-01-01',
      updatedAt: '2024-01-01'
    },
    {
      _id: 'mock-2', 
      name: 'Pusat Perdagangan Sukoharjo',
      description: 'Area komersial strategis dengan potensi tinggi untuk pengembangan pusat perbelanjaan dan perkantoran modern.',
      category: { _id: '2', name: 'Commercial' } as any,
      layer: 'commercial',
      lat: -7.7,
      long: 110.9,
      address: 'Kartasura, Sukoharjo',
      images: [{ _id: '2', url: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=600&h=400&fit=crop', publicId: 'mock', caption: 'Commercial Zone', isPrimary: true }],
      status: 3,
      links: [],
      tags: ['commercial', 'retail'],
      rating: 4.2,
      reviewCount: 18,
      isActive: true,
      isFeatured: true,
      createdAt: '2024-01-01',
      updatedAt: '2024-01-01'
    },
    {
      _id: 'mock-3',
      name: 'Kawasan Agrowisata Terpadu',
      description: 'Pengembangan konsep agrowisata modern yang menggabungkan pertanian, pariwisata, dan edukasi untuk masyarakat.',
      category: { _id: '3', name: 'Agriculture & Tourism' } as any,
      layer: 'agriculture',
      lat: -7.6,
      long: 110.7,
      address: 'Polokarto, Sukoharjo',
      images: [{ _id: '3', url: 'https://images.unsplash.com/photo-1574943320219-553eb213f72d?w=600&h=400&fit=crop', publicId: 'mock', caption: 'Agrotourism', isPrimary: true }],
      status: 1,
      links: [],
      tags: ['agriculture', 'tourism', 'education'],
      rating: 4.0,
      reviewCount: 12,
      isActive: true,
      isFeatured: true,
      createdAt: '2024-01-01',
      updatedAt: '2024-01-01'
    }
  ];

  // Fetch featured POIs for investment opportunities section
  useEffect(() => {
    const fetchFeaturedPOIs = async () => {
      try {
        const response = await poisAPI.getAll({ isFeatured: true, isActive: true, limit: 10 });
        if (response.success && response.data && response.data.length > 0) {
          setFeaturedPOIs(response.data);
          console.log('✅ Featured POIs loaded from API:', response.data.length);
        } else {
          // Use mock data as fallback
          setFeaturedPOIs(mockFeaturedPOIs);
          console.log('📍 Using mock featured POIs data:', mockFeaturedPOIs.length);
        }
      } catch (error) {
        console.error('Error fetching featured POIs, using mock data:', error);
        // Use mock data as fallback
        setFeaturedPOIs(mockFeaturedPOIs);
      }
    };

    fetchFeaturedPOIs();
  }, []);


  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY > 50;
      setIsNavScrolled(scrolled);
      
      // Determine active section based on scroll position
      if (!isScrolling) { // Only update when not actively scrolling
        const sections = ['hero', 'profile', 'facilities', 'opportunities', 'investment-opportunities'];
        const scrollPosition = window.scrollY + 150; // Offset for navbar
        
        for (let i = sections.length - 1; i >= 0; i--) {
          const section = document.getElementById(sections[i]);
          if (section && scrollPosition >= section.offsetTop) {
            setActiveSection(sections[i]);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isScrolling]);

  const regenerateSlideAnimation = useCallback((slideIndex: number) => {
    const randomIndex = Math.floor(Math.random() * animationClasses.length);
    const newAnimation = animationClasses[randomIndex];
    setSlideAnimations(prev => {
      const updated = [...prev];
      updated[slideIndex] = newAnimation;
      return updated;
    });
    console.log(`🎨 Regenerated animation for slide ${slideIndex}:`, newAnimation);
  }, [animationClasses]);

  useEffect(() => {
    const slideInterval = setInterval(() => {
      setCurrentSlide((prev) => {
        const nextIndex = (prev + 1) % activeSlides.length;
        // Regenerate animation for the next slide
        regenerateSlideAnimation(nextIndex);
        return nextIndex;
      });
    }, 12000); // Increased from 8000ms to 12000ms (12 seconds) for much slower transition

    return () => clearInterval(slideInterval);
  }, [activeSlides.length, regenerateSlideAnimation]);

  const nextSlide = () => {
    const nextIndex = (currentSlide + 1) % activeSlides.length;
    setCurrentSlide(nextIndex);
    // Regenerate animation for the next slide
    regenerateSlideAnimation(nextIndex);
  };

  const prevSlide = () => {
    const prevIndex = (currentSlide - 1 + activeSlides.length) % activeSlides.length;
    setCurrentSlide(prevIndex);
    // Regenerate animation for the previous slide
    regenerateSlideAnimation(prevIndex);
  };

  const nextInvestmentOpportunitySlide = () => {
    const sliderRef = (window as any).investmentSliderRef;
    if (sliderRef) {
      const cardWidth = 300; // Approximate card width + gap
      const scrollAmount = cardWidth * 2; // Scroll by 2 cards
      sliderRef.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  const prevInvestmentOpportunitySlide = () => {
    const sliderRef = (window as any).investmentSliderRef;
    if (sliderRef) {
      const cardWidth = 300; // Approximate card width + gap
      const scrollAmount = cardWidth * 2; // Scroll by 2 cards
      sliderRef.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    }
  };

  const scrollToSection = (sectionId: string) => {
    // Prevent multiple scroll actions and auto-triggering
    if (isScrolling) {
      console.log('Scroll already in progress, ignoring:', sectionId);
      return;
    }
    
    const element = document.getElementById(sectionId);
    if (!element) {
      console.log('Element not found:', sectionId);
      return;
    }
    
    console.log('Scrolling to section:', sectionId);
    setIsScrolling(true);
    
    const headerOffset = 80; // Account for fixed navbar
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
    
    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
    
    // Reset scrolling flag after animation with a shorter timeout
    setTimeout(() => {
      setIsScrolling(false);
      setActiveSection(sectionId); // Set active section immediately after scroll
      console.log('Scroll animation completed for:', sectionId);
    }, 800); // Reduced from 1500ms to 800ms
  };

  const handleContactClick = () => {
    navigate('/contact');
  };

  const handleMapClick = () => {
    navigate('/peta-potensi');
  };
  
  const handleSectorClick = (sectorId: number) => {
    const sectorRoutes = {
      1: 'guitar-industry',
      2: 'textile-industry', 
      3: 'agroindustry',
      4: 'furniture-industry',
      5: 'automotive-parts',
      6: 'tourism-development'
    };
    
    const route = sectorRoutes[sectorId as keyof typeof sectorRoutes];
    if (route) {
      navigate(`/sectors/${route}`);
    }
  };

  return (
    <div className="landing-page">
      {/* Navbar */}
      <Navbar 
        onScrollToSection={scrollToSection}
        isScrolled={isNavScrolled}
        isScrolling={isScrolling}
        activeSection={activeSection}
      />

      {/* Hero Section */}
      <section id="hero" className="hero-section">
        <div className="hero-slider">
          {activeSlides.map((slide, index) => {
            const animationClass = slideAnimations[index] || '';
            const isActive = index === currentSlide;
            const slideClasses = `slide ${isActive ? 'active' : ''} ${animationClass}`.trim();
            
            // Debug logging for active slide
            if (isActive && animationClass) {
              console.log(`🎨 Active slide ${index} with animation:`, animationClass);
              console.log(`🎨 Full CSS classes:`, slideClasses);
            }
            
            return (
              <div
                key={slide.id}
                className={slideClasses}
                style={{ backgroundImage: `url(${slide.image})` }}
                data-animation={animationClass} // Add data attribute for debugging
              >
                <div className="slide-overlay">
                  <div className="slide-content">
                    <h1 className="slide-title">{getContentTranslation(slide.title, currentLanguage)}</h1>
                    <p className="slide-subtitle">{getContentTranslation(slide.subtitle, currentLanguage)}</p>
                  </div>
                </div>
              </div>
            );
          })}
          
          <div className="slider-controls">
            <button className="slider-btn prev" onClick={prevSlide}>
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button className="slider-btn next" onClick={nextSlide}>
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>
          
          <div className="slider-indicators">
            {activeSlides.map((_, index) => (
              <button
                key={index}
                className={`indicator ${index === currentSlide ? 'active' : ''}`}
                onClick={() => {
                  setCurrentSlide(index);
                  regenerateSlideAnimation(index);
                }}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Profile Section */}
      <section 
        id="profile" 
        className={`profile-section scroll-reveal ${profileReveal.isVisible ? 'revealed' : ''}`}
        ref={profileReveal.ref}
      >
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{t.profile.title}</h2>
          </div>

          <div className="profile-content">
            <div className="profile-left">
              <div className="profile-image">
                <img src={profileContent.image || "https://picsum.photos/800/600?random=profile"} alt="Sukoharjo Regency" />
              </div>
            </div>
            <div className="profile-right">
              <div className="profile-text">
                <h3>{getContentTranslation(profileContent.title, currentLanguage)}</h3>
                <p>{getContentTranslation(profileContent.description, currentLanguage)}</p>
              </div>
              <div className="profile-advantages">
                <h3>{getContentTranslation(profileContent.whyInvestTitle, currentLanguage)}</h3>
                <div className="advantages-grid">
                  {investmentAdvantages.map((advantage) => (
                    <div 
                      key={advantage.id} 
                      className="advantage-item clickable"
                      onClick={() => {
                        setSelectedAdvantage(advantage);
                        setShowAdvantageModal(true);
                      }}
                    >
                      <div className="advantage-header">
                        {getAdvantageIcon(advantage.icon)}
                        <span className="advantage-title">{getContentTranslation(advantage.title, currentLanguage)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Facilities Section */}
      <section 
        id="facilities" 
        className={`facilities-section scroll-reveal ${facilitiesReveal.isVisible ? 'revealed' : ''}`}
        ref={facilitiesReveal.ref}
      >
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{t.facilities.title}</h2>
            <p className="section-description">{t.facilities.description}</p>
          </div>
          
          <div className="facilities-grid">
            {activeFacilities.map((facility, index) => (
              <div 
                key={facility.id} 
                className={`facility-card scroll-reveal ${visibleFacilities[index] ? 'revealed' : ''}`}
                ref={setFacilityRef(index)}
              >
                <div className="facility-icon">
                  {getFacilityIcon(facility.icon)}
                </div>
                <h3 className="facility-title">{getContentTranslation(facility.name, currentLanguage)}</h3>
                <p className="facility-description">{getContentTranslation(facility.description, currentLanguage)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Opportunities Section - Back to Grid Layout */}
      <section 
        id="opportunities" 
        className={`opportunities-section scroll-reveal ${opportunitiesReveal.isVisible ? 'revealed' : ''}`}
        ref={opportunitiesReveal.ref}
      >
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{t.opportunities.title}</h2>
            <p className="section-description">{t.opportunities.description}</p>
          </div>
          
          <div className="opportunities-grid">
            {activeOpportunities.map((opportunity, index) => (
              <div 
                key={opportunity.id} 
                className={`opportunity-card scroll-reveal clickable ${visibleOpportunities[index] ? 'revealed' : ''}`}
                ref={setOpportunityRef(index)}
                onClick={() => handleSectorClick(opportunity.id)}
              >
                <div className="opportunity-image">
                  <img src={opportunity.image} alt={getContentTranslation(opportunity.title, currentLanguage)} />
                </div>
                <div className="opportunity-content">
                  <h3 className="opportunity-title">{getContentTranslation(opportunity.title, currentLanguage)}</h3>
                  <p className="opportunity-description">{getContentTranslation(opportunity.description, currentLanguage)}</p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="opportunities-view-all-container">
            <button 
              className="btn btn-outline"
              onClick={() => navigate('/semua-peluang-investasi')}
            >
              {currentLanguage === 'id' ? 'Lihat Semua' : 'View All'}
            </button>
          </div>
        </div>
      </section>

      {/* Investment Opportunities Section - Reference Design */}
      <section 
        id="investment-opportunities" 
        className={`investment-opportunities-section-reference scroll-reveal ${investmentOpportunitiesReveal.isVisible ? 'revealed' : ''}`}
        ref={investmentOpportunitiesReveal.ref}
      >
        <div className="container">
          {/* Main section title */}
          <div className="section-header-simple">
            <h2 className="section-title-reference">{t.investmentOpportunities.title}</h2>
            <p className="section-subtitle-reference">{t.investmentOpportunities.description}</p>
          </div>
          
          {featuredPOIs.length > 0 ? (
            <>
              {/* Layout with fixed highlighted card on left and scrolling cards on right */}
              <div className="investment-opportunities-layout">
                {/* Fixed highlighted card on the left */}
                <div className="highlighted-card-container">
                  <div 
                    className="investment-opportunity-ref-card highlighted clickable"
                    onClick={() => navigate(`/peta-potensi?poi=${featuredPOIs[currentInvestmentOpportunitySlide]._id}`)}
                  >
                    <div className="investment-opportunity-ref-image">
                      <img 
                        src={featuredPOIs[currentInvestmentOpportunitySlide]?.primaryImage?.url || 
                             featuredPOIs[currentInvestmentOpportunitySlide]?.images?.[0]?.url || 
                             'https://picsum.photos/400/300?random=' + featuredPOIs[currentInvestmentOpportunitySlide]._id} 
                        alt={featuredPOIs[currentInvestmentOpportunitySlide]?.name} 
                      />
                    </div>
                    <div className="investment-opportunity-ref-content">
                      <div className="investment-opportunity-ref-meta">
                        <span className="investment-opportunity-ref-category">
                          {(() => {
                            const currentPOI = featuredPOIs[currentInvestmentOpportunitySlide];
                            if (typeof currentPOI?.category === 'object') {
                              return currentPOI.category.name;
                            }
                            return currentPOI?.category || 'AREA';
                          })()}
                        </span>
                        <span className="investment-opportunity-ref-time">Featured</span>
                      </div>
                      <h4 className="investment-opportunity-ref-title">{featuredPOIs[currentInvestmentOpportunitySlide]?.name}</h4>
                      <p className="investment-opportunity-ref-description">{featuredPOIs[currentInvestmentOpportunitySlide]?.description}</p>
                      {featuredPOIs[currentInvestmentOpportunitySlide]?.rating > 0 && (
                        <div className="poi-rating">
                          <span className="rating-stars">{'★'.repeat(Math.floor(featuredPOIs[currentInvestmentOpportunitySlide].rating))}</span>
                          <span className="rating-value">{featuredPOIs[currentInvestmentOpportunitySlide].rating.toFixed(1)} ({featuredPOIs[currentInvestmentOpportunitySlide].reviewCount} reviews)</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Right side with title, subtitle and scrolling cards */}
                <div className="scrolling-cards-container">
                  {/* Title and navigation above scrolling cards */}
                  <div className="highlighted-card-header">
                    <div className="highlighted-card-info">
                      <h3 className="highlighted-title-small">
                        {featuredPOIs[currentInvestmentOpportunitySlide]?.name || 'Investment Opportunity'}
                      </h3>
                      <p className="highlighted-subtitle-small">
                        {featuredPOIs[currentInvestmentOpportunitySlide]?.address || 'Sukoharjo, Jawa Tengah'}
                      </p>
                    </div>
                    <div className="slider-navigation">
                      <button 
                        className="slider-nav-btn prev" 
                        onClick={() => {
                          setCurrentInvestmentOpportunitySlide(prev => 
                            prev > 0 ? prev - 1 : featuredPOIs.length - 1
                          );
                        }}
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                      <button 
                        className="slider-nav-btn next" 
                        onClick={() => {
                          setCurrentInvestmentOpportunitySlide(prev => 
                            prev < featuredPOIs.length - 1 ? prev + 1 : 0
                          );
                        }}
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Scrolling small cards */}
                  <div className="investment-opportunities-slider-container-small">
                    <div 
                      className="investment-opportunities-slider-track-small"
                      style={{
                        transform: `translateX(-${Math.max(0, currentInvestmentOpportunitySlide - 1) * 320}px)`
                      }}
                    >
                      {featuredPOIs
                        .filter((_, index) => index !== currentInvestmentOpportunitySlide) // Exclude highlighted card
                        .map((poi, filteredIndex) => {
                          // Calculate original index to maintain proper click behavior
                          const originalIndex = featuredPOIs.findIndex(p => p._id === poi._id);
                          return (
                            <div 
                              key={poi._id} 
                              className="investment-opportunity-ref-card normal clickable"
                              onClick={() => {
                                setCurrentInvestmentOpportunitySlide(originalIndex);
                              }}
                            >
                              <div className="investment-opportunity-ref-image">
                                <img 
                                  src={poi.primaryImage?.url || poi.images?.[0]?.url || 'https://picsum.photos/400/300?random=' + poi._id} 
                                  alt={poi.name} 
                                />
                              </div>
                              <div className="investment-opportunity-ref-content">
                                <div className="investment-opportunity-ref-meta">
                                  <span className="investment-opportunity-ref-category">
                                    {typeof poi.category === 'object' ? poi.category.name : poi.category || 'AREA'}
                                  </span>
                                  <span className="investment-opportunity-ref-time">Featured</span>
                                </div>
                                <h4 className="investment-opportunity-ref-title">{poi.name}</h4>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="no-opportunities">
              <p>No featured investment opportunities available at the moment.</p>
              <p className="text-sm text-gray-500 mt-2">Featured POIs will appear here when they are marked as featured in the admin panel.</p>
            </div>
          )}
        </div>
      </section>

      {/* Call to Action Section */}
      <section 
        className={`cta-section scroll-reveal ${ctaReveal.isVisible ? 'revealed' : ''}`}
        ref={ctaReveal.ref}
      >
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">{t.cta.title}</h2>
            <p className="cta-description">{t.cta.description}</p>
            <div className="cta-buttons">
              <button className="btn btn-primary" onClick={handleMapClick}>
                <MapPin className="w-5 h-5" />
                {t.cta.viewMap}
              </button>
              <button className="btn btn-secondary" onClick={handleContactClick}>
                <Mail className="w-5 h-5" />
                {t.cta.contactUs}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <img src={websiteSettings?.logo || "/logo192.png"} alt={websiteSettings?.siteName || "RIS Logo"} className="footer-logo" />
              <p className="footer-description">{footerContent?.description || t.footer.description}</p>
            </div>
            
            <div className="footer-contact">
              <h4>{t.footer.contactInfo}</h4>
              <div className="contact-info-simple">
                <p><strong>{footerContent?.contactInfo?.office || t.footer.office}</strong></p>
                <p>{footerContent?.contactInfo?.address || t.footer.addressFull}</p>
                <p>Phone: {footerContent?.contactInfo?.phone || "+62 271 593156"}</p>
                <p>Email: {footerContent?.contactInfo?.email || "dpmptsp@sukoharjokab.go.id"}</p>
              </div>
            </div>
          </div>
          
          <div className="footer-bottom">
            <p>{footerContent?.copyright || t.footer.copyright}</p>
          </div>
        </div>
      </footer>

      {/* Advantage Modal */}
      {showAdvantageModal && selectedAdvantage && (
        <div className="modal-overlay" onClick={() => setShowAdvantageModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-container">
                <div className="advantage-modal-icon">
                  {(() => {
                    const icon = getAdvantageIcon(selectedAdvantage.icon);
                    if (React.isValidElement(icon)) {
                      return React.cloneElement(icon, { className: 'w-6 h-6' } as any);
                    }
                    return <TrendingUp className="w-6 h-6" />;
                  })()}
                </div>
                <h3 className="modal-title">{getContentTranslation(selectedAdvantage.title, currentLanguage)}</h3>
              </div>
              <button className="modal-close" onClick={() => setShowAdvantageModal(false)}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <p className="modal-description">{getContentTranslation(selectedAdvantage.description, currentLanguage)}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;